import { useState, useMemo, useEffect, cloneElement, isValidElement, type ReactElement } from 'react';
import {
  SimpleForm,
  AutocompleteInput,
  required,
  useGetList,
  useTranslate,
  useNotify,
  SaveButton,
  Toolbar,
  useDataProvider,
} from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  InputAdornment,
  Box,
  Typography,
  Alert,
  Stack,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { ReferenceInput } from '../../../../components';
import { ChannelType, ChannelConnectionStatus } from '../../../../types/chat.types';
import { ChannelTypeIcon } from '../../../contact/components/ChannelTypeIcon';
import { ZapyTicketForm, TelegramTicketForm } from './channels';

interface ChannelConnection {
  id: number;
  uuid: string;
  channelId: number;
  status?: ChannelConnectionStatus;
  displayName?: string | null;
}

interface Channel {
  id: number;
  uuid: string;
  name: string;
  type: ChannelType;
  isEnabled: boolean;
  connections?: ChannelConnection[];
}

// Shared contact choice interface for all channel types
interface ContactChoice {
  id: string;
  name: string;
  contactName: string | null;
  contactId?: number;
  contactIdentityId?: number;
}

export interface CreateConversationDialogProps {
  open?: boolean;
  onClose?: () => void;
  onSuccess?: (conversationId: number, conversationUuid: string) => void;
  /** Custom trigger element. If provided, clicking it opens the dialog. */
  trigger?: ReactElement;
  /** Custom dialog title. Defaults to 'resources.conversation.actions.create' translation. */
  title?: string;
}

/**
 * CreateConversationDialog - Dialog for creating tickets across different channels
 *
 * Supports:
 * - Zapy (WhatsApp): Phone number search, can initiate with new contacts
 * - Telegram: Contact search, can only message users who have messaged the bot first
 *
 * Flow:
 * 1. User selects a channel
 * 2. Channel-specific form is rendered based on channel type
 * 3. For Zapy: phone number search with new contact creation
 * 4. For Telegram: search existing contacts that have talked to the bot
 * 5. Creates ticket via POST /ticket/from-contact
 */
export const CreateConversationDialog = ({
  open: externalOpen,
  onClose: externalOnClose,
  onSuccess,
  trigger,
  title,
}: CreateConversationDialogProps = {}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const [internalOpen, setInternalOpen] = useState(false);
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = externalOnClose ? (value: boolean) => !value && externalOnClose() : setInternalOpen;

  const handleTriggerClick = () => {
    setInternalOpen(true);
  };

  // Render trigger element with click handler
  const renderTrigger = () => {
    if (trigger && isValidElement(trigger)) {
      const existingOnClick = (trigger.props as { onClick?: (e: React.MouseEvent) => void }).onClick;
      return cloneElement(trigger, {
        onClick: (e: React.MouseEvent) => {
          handleTriggerClick();
          existingOnClick?.(e);
        },
      });
    }

    // Default trigger button
    return (
      <Tooltip title={translate('resources.conversation.actions.create')}>
        <IconButton onClick={handleTriggerClick} size="small" color="primary">
          <AddIcon />
        </IconButton>
      </Tooltip>
    );
  };
  const [selectedChannelId, setSelectedChannelId] = useState<number | null>(null);
  const [selectedContact, setSelectedContact] = useState<ContactChoice | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch all enabled channels (not just Zapy)
  const { data: channels } = useGetList<Channel>('channel', {
    pagination: { page: 1, perPage: 100 },
    filter: { isEnabled_eq: true },
  });

  // Fetch channel connections for selected channel
  const { data: channelConnections } = useGetList<ChannelConnection>('channel-connection', {
    pagination: { page: 1, perPage: 100 },
    filter: selectedChannelId ? { channelId_eq: selectedChannelId } : {},
  }, { enabled: !!selectedChannelId });

  // Get first connected channel connection
  const activeConnection = useMemo(() => {
    if (!channelConnections) return null;
    return channelConnections.find(c => c.status === ChannelConnectionStatus.CONNECTED)
      ?? channelConnections[0];
  }, [channelConnections]);

  // Get first channel ID for default selection
  const firstChannelId = channels?.[0]?.id ?? null;

  // Auto-select first channel when modal opens
  useEffect(() => {
    if (open && firstChannelId && !selectedChannelId) {
      setSelectedChannelId(firstChannelId);
    }
  }, [open, firstChannelId, selectedChannelId]);

  const selectedChannel = channels?.find((c) => c.id === selectedChannelId);
  const channelType = selectedChannel?.type;

  const handleChannelChange = (value: number | null) => {
    setSelectedChannelId(value);
    setSelectedContact(null);
  };

  const handleContactSelect = (choice: ContactChoice | null) => {
    setSelectedContact(choice);
  };

  const resetState = () => {
    setSelectedChannelId(null);
    setSelectedContact(null);
  };

  const handleClose = () => {
    setOpen(false);
    resetState();
  };

  const handleSubmit = async (data: Record<string, unknown>) => {
    if (!activeConnection) {
      notify('resources.conversation.errors.noConnection', { type: 'error' });
      return;
    }

    if (!selectedContact) {
      notify('resources.conversation.errors.noContact', { type: 'error' });
      return;
    }

    setIsSubmitting(true);

    try {
      let contactId = selectedContact.contactId;

      // For Zapy: If no existing contact, create one first
      if (channelType === ChannelType.ZAPY && !contactId) {
        const phoneNumber = data.phoneNumber as string;
        const contactName = (data.contactName as string) || selectedContact.contactName || phoneNumber;

        const contactResponse = await dataProvider.create('contact', {
          data: {
            phoneNumber,
            name: contactName,
          },
        });
        contactId = contactResponse.data.id;
      }

      if (!contactId) {
        notify('resources.conversation.errors.noContact', { type: 'error' });
        return;
      }

      // Create ticket from contact
      const ticketResponse = await dataProvider.create('ticket/from-contact', {
        data: {
          channelConnectionId: activeConnection.id,
          contactId,
          // For Zapy, pass phone number to update/verify
          ...(channelType === ChannelType.ZAPY && data.phoneNumber
            ? { phoneNumber: data.phoneNumber }
            : {}),
        },
      });

      const isExisting = ticketResponse?.meta?.isExisting;

      if (isExisting) {
        notify('resources.ticket.notifications.existingTicketOpened', { type: 'info' });
      } else {
        notify('ra.notification.created', { type: 'info' });
      }

      handleClose();

      const conversationId = ticketResponse.data?.conversationId;
      const conversationUuid = ticketResponse.data?.conversation?.uuid;

      if (onSuccess && conversationId && conversationUuid) {
        onSuccess(conversationId, conversationUuid);
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'ra.notification.http_error';
      notify(errorMessage, { type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const FormToolbar = () => (
    <Toolbar>
      <SaveButton
        label={translate('ra.action.create')}
        disabled={isSubmitting || !activeConnection || !selectedContact}
      />
    </Toolbar>
  );

  // Render channel-specific form based on channel type
  const renderChannelForm = () => {
    if (!selectedChannelId || !activeConnection) return null;

    switch (channelType) {
      case ChannelType.ZAPY:
        return (
          <ZapyTicketForm
            channelConnectionId={activeConnection.id}
            onContactSelect={handleContactSelect}
            selectedContact={selectedContact}
          />
        );

      case ChannelType.TELEGRAM:
        return (
          <TelegramTicketForm
            channelConnectionId={activeConnection.id}
            onContactSelect={handleContactSelect}
            selectedContact={selectedContact}
          />
        );

      default:
        return (
          <Alert severity="warning">
            {translate('resources.conversation.errors.unsupportedChannel')}
          </Alert>
        );
    }
  };

  // Only render trigger when dialog is NOT externally controlled
  const isControlled = externalOpen !== undefined;

  return (
    <>
      {/* Trigger button (only in uncontrolled mode) */}
      {!isControlled && renderTrigger()}

      {/* Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {title || translate('resources.conversation.actions.create')}
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <SimpleForm
          onSubmit={handleSubmit}
          toolbar={<FormToolbar />}
          defaultValues={{ channelId: firstChannelId }}
        >
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="h6" gutterBottom>
              {translate('resources.conversation.dialogs.create.channel')}
            </Typography>

            <ReferenceInput
              source="channelId"
              reference="channel"
              filter={{ isEnabled_eq: true }}
            >
              <AutocompleteInput
                label={translate('resources.conversation.fields.channel')}
                validate={required()}
                fullWidth
                onChange={(value: number | null) => handleChannelChange(value)}
                optionText={(record: Channel) => (
                  <Box display="flex" alignItems="center" gap={1}>
                    <ChannelTypeIcon type={record?.type} />
                    {record?.name}
                  </Box>
                )}
                inputText={(record: Channel) => record?.name ?? ''}
                matchSuggestion={(filter: string, choice: Channel) =>
                  choice?.name?.toLowerCase().includes(filter.toLowerCase()) ?? false
                }
                TextFieldProps={{
                  InputProps: selectedChannel ? {
                    startAdornment: (
                      <InputAdornment position="start">
                        <ChannelTypeIcon type={selectedChannel.type} />
                      </InputAdornment>
                    ),
                  } : undefined,
                }}
              />
            </ReferenceInput>

            {/* Show warning if no connection available */}
            {selectedChannelId && !activeConnection && channelConnections && (
              <Alert severity="warning">
                {translate('resources.conversation.errors.noConnectionAvailable')}
              </Alert>
            )}

            {/* Render channel-specific form */}
            {renderChannelForm()}
          </Stack>
        </SimpleForm>
      </DialogContent>
      </Dialog>
    </>
  );
};

export default CreateConversationDialog;
